/**
 * Shared filter-key types and parsing helpers used by both the V1 store
 * (`apps/oak/src/stores/omnisearchStore.ts`) and the V2 store
 * (`apps/oak/src/hooks/omnisearch-v2/omnisearchStore.ts`).
 *
 * These were previously copy-pasted across both stores (~200 lines of pure
 * duplication, see REVIEW.md item #3). Centralizing here removes the
 * duplication and the behavioral fork around date-bounding.
 */

import {
  RegulatoryStatusOption,
  SubmissionStatus,
  WasteSubmissionsSummaryFilter,
} from "generated-graphql/graphql";
import { DateTime } from "luxon";

// US states for validation
const US_STATES = [
  "AL",
  "AK",
  "AZ",
  "AR",
  "CA",
  "CO",
  "CT",
  "DE",
  "FL",
  "GA",
  "HI",
  "ID",
  "IL",
  "IN",
  "IA",
  "KS",
  "KY",
  "LA",
  "ME",
  "MD",
  "MA",
  "MI",
  "MN",
  "MS",
  "MO",
  "MT",
  "NE",
  "NV",
  "NH",
  "NJ",
  "NM",
  "NY",
  "NC",
  "ND",
  "OH",
  "OK",
  "OR",
  "PA",
  "RI",
  "SC",
  "SD",
  "TN",
  "TX",
  "UT",
  "VT",
  "VA",
  "WA",
  "WV",
  "WI",
  "WY",
  "DC",
  "PR",
  "VI",
  "GU",
  "AS",
  "MP",
];

const ALL_STATUSES = Object.values(SubmissionStatus);
const ALL_GENERATOR_STATUSES = Object.values(RegulatoryStatusOption);

/**
 * Filter keys that affect chart queries (NOT pagination/sorting).
 * When these change, charts should refetch.
 */
export interface FilterKeys {
  dueDateStart: string | null;
  dueDateEnd: string | null;
  states: string[] | null;
  statuses: SubmissionStatus[] | null;
  generatorStatuses: RegulatoryStatusOption[] | null;
  facilityTagNames: string[] | null;
  readyForActionEmail: string | null;
}

export const INITIAL_FILTER_KEYS: FilterKeys = {
  dueDateStart: null,
  dueDateEnd: null,
  states: null,
  statuses: null,
  generatorStatuses: null,
  facilityTagNames: null,
  readyForActionEmail: null,
};

/** Extract a key's value from an omnisearch string. */
export function extractOmnisearchValue(omnisearch: string | null, key: string): string | undefined {
  if (!omnisearch) return undefined;
  const regex = new RegExp(`(?:^|\\s)${key}:(?:"([^"]+)"|([^\\s]+))`, "i");
  const match = omnisearch.match(regex);
  return match ? (match[1] ?? match[2]) : undefined;
}

/**
 * Parse omnisearch string into filter keys.
 *
 * IMPORTANT — date semantics:
 * - Date-only strings (`2026-01-01`) are parsed in UTC to match how the
 *   datagrid resolver parses dates (`new Date("2026-01-01")` is midnight UTC).
 *   Without forcing UTC, Luxon defaults to local time and events at midnight
 *   UTC get excluded from chart counts but included in the grid.
 * - The end of a date range is widened to end-of-day so that selecting
 *   "2026-01-01 to 2026-01-31" matches every event on the 31st instead of
 *   stopping at midnight (which would exclude the entire last day).
 *   This matches the V1 store's production-tested behavior. The V2 store
 *   used to drop this widening — that drift caused last-day events to be
 *   missed from chart filters.
 * - An unreadable date is passed through as typed, so the server matches
 *   nothing for it, just as it does for the grid.
 */
export function parseFilterKeys(omnisearch: string): FilterKeys {
  const filterKeys: FilterKeys = { ...INITIAL_FILTER_KEYS };

  if (!omnisearch) return filterKeys;

  const dueDateVal = extractOmnisearchValue(omnisearch, "dueDate");
  if (dueDateVal) {
    const [startStr, endStr] = dueDateVal.split("..");
    if (startStr) {
      const start = DateTime.fromISO(startStr, { zone: "utc" });
      filterKeys.dueDateStart = start.isValid ? start.startOf("day").toISO() : startStr;
    }
    if (endStr) {
      const end = DateTime.fromISO(endStr, { zone: "utc" });
      filterKeys.dueDateEnd = end.isValid ? end.endOf("day").toISO() : endStr;
    }
  }

  const statesVal = extractOmnisearchValue(omnisearch, "states");
  if (statesVal && statesVal !== "none") {
    const states = statesVal.split(/[|,]/).filter((s) => s.length > 0 && US_STATES.includes(s));
    if (states.length > 0 && states.length < US_STATES.length) {
      filterKeys.states = states;
    }
  }

  const statusVal = extractOmnisearchValue(omnisearch, "submissionStatus");
  if (statusVal && statusVal !== "none") {
    const statuses = statusVal
      .split(/[|,]/)
      .filter((s) => s.length > 0 && ALL_STATUSES.includes(s as SubmissionStatus));
    if (statuses.length > 0 && statuses.length < ALL_STATUSES.length) {
      filterKeys.statuses = statuses as SubmissionStatus[];
    }
  }

  const genStatusVal = extractOmnisearchValue(omnisearch, "generatorStatus");
  if (genStatusVal) {
    const genStatuses = genStatusVal
      .split(/[|,]/)
      .filter((s) => s.length > 0 && ALL_GENERATOR_STATUSES.includes(s as RegulatoryStatusOption));
    if (genStatuses.length > 0 && genStatuses.length < ALL_GENERATOR_STATUSES.length) {
      filterKeys.generatorStatuses = genStatuses as RegulatoryStatusOption[];
    }
  }

  const tagsVal = extractOmnisearchValue(omnisearch, "facilityTags");
  if (tagsVal) {
    const tags = tagsVal.split(/[|,]/).filter((t) => t.length > 0);
    if (tags.length > 0) {
      filterKeys.facilityTagNames = tags;
    }
  }

  const readyForActionVal = extractOmnisearchValue(omnisearch, "readyForAction");
  if (readyForActionVal) {
    filterKeys.readyForActionEmail = readyForActionVal;
  }

  return filterKeys;
}

/** Deep equality check for FilterKeys. */
export function filterKeysEqual(a: FilterKeys, b: FilterKeys): boolean {
  return (
    a.dueDateStart === b.dueDateStart &&
    a.dueDateEnd === b.dueDateEnd &&
    a.readyForActionEmail === b.readyForActionEmail &&
    arraysEqual(a.states, b.states) &&
    arraysEqual(a.statuses, b.statuses) &&
    arraysEqual(a.generatorStatuses, b.generatorStatuses) &&
    arraysEqual(a.facilityTagNames, b.facilityTagNames)
  );
}

export function arraysEqual<T>(a: T[] | null, b: T[] | null): boolean {
  if (a === null && b === null) return true;
  if (a === null || b === null) return false;
  if (a.length !== b.length) return false;
  const sortedA = [...a].sort();
  const sortedB = [...b].sort();
  return sortedA.every((val, idx) => val === sortedB[idx]);
}

/**
 * Convert FilterKeys to the GraphQL filter shape consumed by chart queries.
 * Returns undefined when no filter keys are populated.
 */
export function buildFilterFromKeys(
  filterKeys: FilterKeys
): WasteSubmissionsSummaryFilter | undefined {
  const filter: WasteSubmissionsSummaryFilter = {};

  if (filterKeys.dueDateStart) filter.dueDateStart = filterKeys.dueDateStart;
  if (filterKeys.dueDateEnd) filter.dueDateEnd = filterKeys.dueDateEnd;
  if (filterKeys.states) filter.states = filterKeys.states;
  if (filterKeys.statuses) filter.statuses = filterKeys.statuses;
  if (filterKeys.generatorStatuses) filter.generatorStatuses = filterKeys.generatorStatuses;
  if (filterKeys.facilityTagNames) filter.facilityTagNames = filterKeys.facilityTagNames;
  if (filterKeys.readyForActionEmail) filter.readyForActionEmail = filterKeys.readyForActionEmail;

  return Object.keys(filter).length > 0 ? filter : undefined;
}
